import Arrow from 'assets/svg/arrow/arrow.svg'
import AutoComplete from 'common/components/AutoComplete'
import Button from 'common/components/Button'
import Drawer from 'common/components/Drawer'
import Typography from 'common/components/Typography'
import theme from 'common/theme'
import camelToSnakeKeys from 'lib/transform/camel-to-snake-case'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useStoreActions, useStoreState } from 'stores/hooks'
import styled from 'styled-components'

const FilterContainer = styled.div`
  padding: 0 50px;
`

const FilterHeading = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 30px 0;
  border-bottom: 1px solid ${theme.colors.grayLight};
`

const IconClose = styled.span`
  font-weight: ${theme.typography.bh2.fontWeight};
  font-size: ${theme.typography.bh2.fontSize};
  font-stretch: ${theme.typography.bh2.fontStretch};
  font-style: ${theme.typography.bh2.fontStyle};
  letter-spacing: ${theme.typography.bh2.letterSpacing};
  line-height: ${theme.typography.bh2.lineHeight};
  cursor: pointer;
`

const InputGroup = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
`

const Icon = styled.span`
  height: 100%;
  align-items: center;
  svg {
    path {
      fill: ${({ isActive }) =>
        isActive ? theme.colors.primaryKonekto : theme.colors.black};
    }
  }
`

const FilterBottom = styled.div`
  display: flex;
  position: absolute;
  bottom: 0;
  padding-bottom: 30px;
`

const StyledButton = styled(Button)`
  background-color: ${theme.colors.grayLighter};
  color: ${theme.colors.black};
  font-weight: bold;
`
interface IFilterDrawer {
  isOpen: boolean
  onClose: Function
}

function FilterDrawer({
  isOpen,
  onClose,
}: IFilterDrawer): JSX.Element {
  // HOOKS
  const router = useRouter()

  // LOCAL STATE
  const [focusField, setFocusField] = useState<string>('')
  const [filterProvince, setFilterProvince] = useState('')
  const [filterCity, setFilterCity] = useState('')
  const [filterBusinessType, setFilterBusinessType] = useState('')
  const [filterSubBusinessType, setFilterSubBusinessType] = useState(
    '',
  )

  const [selectedProvince, setSelectedProvince] = useState('')
  const [selectedCity, setSelectedCity] = useState('')
  const [selectedBusinessType, setSelectedBusinessType] = useState('')
  const [
    selectedSubBusinessType,
    setSelectedSubBusinessType,
  ] = useState('')

  // STORE STATE
  const {
    ralaliConnect: {
      errorProvince,
      errorCity,
      errorBusinessType,
      errorSubBusinessType,
      provinces,
      cities,
      businessTypes,
      subBusinessTypes,
      isErrorProvince,
      isErrorCity,
      isErrorBusinessType,
      isErrorSubBusinessType,
    },
  } = useStoreState((states) => states)

  // STORE ACTION
  const {
    ralaliConnect: {
      getCities,
      getSubBusinessTypes,
      setCities,
      setSubBusinessTypes,
    },
  } = useStoreActions((actions) => actions)

  // VARIABLE
  const queryParams = useMemo(() => {
    return {
      storeName: router.query?.q,
      provinceId: router.query?.province_id,
      cityId: router.query?.city_id,
      businessTypeId: router.query?.business_type_id,
      subBusinessTypeId: router.query?.sub_business_type_id,
    } as any
  }, [router.query])

  const filteredProvinces = useMemo(() => {
    return provinces
      .filter((province) =>
        province.name
          .toLowerCase()
          .includes(filterProvince.toLocaleLowerCase()),
      )
      .map((province) => ({
        value: province.id,
        label: province.name,
      }))
  }, [filterProvince, provinces])

  const filteredCities = useMemo(() => {
    if (!filterProvince && !selectedProvince) return []
    return cities
      .filter((city) =>
        city.name
          .toLocaleLowerCase()
          .includes(filterCity.toLocaleLowerCase()),
      )
      .map((city) => ({
        value: city.id,
        label: city.name,
      }))
  }, [cities, filterCity, filterProvince, selectedProvince])

  const filteredBusinessTypes = useMemo(() => {
    return businessTypes
      .filter((businessType) =>
        businessType.name
          .toLowerCase()
          .includes(filterBusinessType.toLocaleLowerCase()),
      )
      .map((businessType) => ({
        value: businessType.id,
        label: businessType.name,
      }))
  }, [businessTypes, filterBusinessType])

  const filteredSubBusinessTypes = useMemo(() => {
    if (!selectedBusinessType && !selectedSubBusinessType) return []
    return subBusinessTypes
      .filter((subBusinessType) =>
        subBusinessType.name
          .toLowerCase()
          .includes(filterSubBusinessType.toLocaleLowerCase()),
      )
      .map((subBusinessType) => ({
        value: subBusinessType.id,
        label: subBusinessType.name,
      }))
  }, [
    selectedBusinessType,
    selectedSubBusinessType,
    subBusinessTypes,
    filterSubBusinessType,
  ])

  // METHOD
  const fetchCities = useCallback(
    async (provinceId) => {
      await getCities({
        provinceId,
        limit: 50,
      })
    },
    [getCities],
  )

  const fetchSubBusinessTypes = useCallback(
    async (businessTypeId) => {
      await getSubBusinessTypes({
        limit: 50,
        businessTypeId,
      })
    },
    [getSubBusinessTypes],
  )

  const handleTypingProvince = useCallback((value) => {
    if (value === '') {
      setFilterCity('')
    }
    setFilterProvince(value)
  }, [])

  const handleSelectProvince = useCallback(
    async (value) => {
      setCities([])
      setFilterCity('')
      setSelectedCity('')
      setSelectedProvince(value)
      await fetchCities(value)
    },
    [fetchCities, setCities],
  )

  const handleTypingCity = useCallback((value) => {
    setFilterCity(value)
  }, [])

  const handleTypingBusinessType = (value) => {
    if (value === '') {
      setFilterSubBusinessType('')
    }
    setFilterBusinessType(value)
  }

  const handleSelectBusinessType = useCallback(
    async (value) => {
      setSubBusinessTypes([])
      setFilterSubBusinessType('')
      setSelectedBusinessType(value)
      await fetchSubBusinessTypes(value)
    },
    [fetchSubBusinessTypes, setSubBusinessTypes],
  )

  const handleTypingSubBusinessType = useCallback((value) => {
    setFilterSubBusinessType(value)
  }, [])

  const resetFilter = useCallback(() => {
    setSelectedProvince('')
    setSelectedCity('')
    setSelectedBusinessType('')
    setSelectedSubBusinessType('')
    router.push({
      pathname: '/local-store/search',
      query: { q: queryParams.storeName },
    })
  }, [queryParams, router])

  const submitFilter = useCallback(() => {
    const { storeName } = queryParams
    router.push({
      pathname: '/local-store/search',
      query: camelToSnakeKeys({
        ...(storeName && { q: storeName }),
        ...(selectedProvince && { province_id: selectedProvince }),
        ...(selectedCity && { city_id: selectedCity }),
        ...(selectedBusinessType && {
          business_type_id: selectedBusinessType,
        }),
        ...(selectedSubBusinessType && {
          sub_business_type_id: selectedSubBusinessType,
        }),
      }),
    })
  }, [
    queryParams,
    router,
    selectedBusinessType,
    selectedCity,
    selectedProvince,
    selectedSubBusinessType,
  ])

  // USE EFFECT
  useEffect(() => {
    if (router.query?.province_id) {
      setSelectedProvince(String(router?.query?.province_id))
    }

    if (router.query?.city_id && router.query?.province_id) {
      setSelectedCity(String(router?.query?.city_id))
    }

    if (router.query?.business_type_id) {
      setSelectedBusinessType(String(router?.query?.business_type_id))
    }

    if (
      router.query?.business_type_id &&
      router.query?.sub_business_type_id
    ) {
      setSelectedSubBusinessType(
        String(router.query.sub_business_type_id),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query, queryParams])
  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      contentProps={{
        anchor: 'right',
        height: 'calc(100% - 90px)',
        width: '570px',
      }}
    >
      <FilterContainer>
        <FilterHeading>
          <Typography type="bh2">Filter</Typography>
          <IconClose onClick={onClose}>&times;</IconClose>
        </FilterHeading>
        <InputGroup>
          <span>Provinsi</span>
          <AutoComplete
            onblur={() => setFocusField('')}
            onfocus={() => setFocusField('province')}
            options={filteredProvinces}
            value={selectedProvince}
            onSelect={handleSelectProvince}
            onTyping={handleTypingProvince}
            onHoverColor="primaryKonekto"
            padding="13px"
            placeholder="Pilih Provinsi"
            width="300px"
            hideHelperText={!isErrorProvince}
            helperText={errorProvince}
            error={isErrorProvince}
            withIcon={{
              icon: (
                <Icon isActive={focusField === 'province'}>
                  <Arrow
                    transform-origin="50% 50%"
                    transform={`rotate(${
                      focusField === 'province' ? 0 : 180
                    })`}
                  />
                </Icon>
              ),
              position: 'right',
            }}
            borderActiveColor={theme.colors.primaryKonekto}
          />
        </InputGroup>
        {cities.length > 0 && (
          <InputGroup>
            <span>Kota</span>
            <AutoComplete
              onblur={() => setFocusField('')}
              onfocus={() => setFocusField('city')}
              disabled={cities.length === 0}
              options={filteredCities}
              value={selectedCity}
              onTyping={handleTypingCity}
              onSelect={setSelectedCity}
              onHoverColor="primaryKonekto"
              padding="13px"
              placeholder="Pilih Kota"
              width="300px"
              hideHelperText={!isErrorCity}
              helperText={errorCity}
              error={isErrorCity}
              withIcon={{
                icon: (
                  <Icon isActive={focusField === 'city'}>
                    <Arrow
                      transform-origin="50% 50%"
                      transform={`rotate(${
                        focusField === 'city' ? 0 : 180
                      })`}
                    />
                  </Icon>
                ),
                position: 'right',
              }}
              borderActiveColor={theme.colors.primaryKonekto}
            />
          </InputGroup>
        )}
        <InputGroup>
          <span>Tipe Bisnis</span>
          <AutoComplete
            onblur={() => setFocusField('')}
            onfocus={() => setFocusField('business_type')}
            options={filteredBusinessTypes}
            value={selectedBusinessType}
            onSelect={handleSelectBusinessType}
            onTyping={handleTypingBusinessType}
            onHoverColor="primaryKonekto"
            padding="13px"
            placeholder="Pilih Tipe Bisnis"
            width="300px"
            hideHelperText={!isErrorBusinessType}
            helperText={errorBusinessType}
            error={isErrorBusinessType}
            withIcon={{
              icon: (
                <Icon isActive={focusField === 'business_type'}>
                  <Arrow
                    transform-origin="50% 50%"
                    transform={`rotate(${
                      focusField === 'business_type' ? 0 : 180
                    })`}
                  />
                </Icon>
              ),
              position: 'right',
            }}
            borderActiveColor={theme.colors.primaryKonekto}
          />
        </InputGroup>
        {subBusinessTypes.length > 0 && (
          <InputGroup>
            <span>Sub Tipe Bisnis</span>
            <AutoComplete
              onblur={() => setFocusField('')}
              onfocus={() => setFocusField('sub_business_type')}
              disabled={subBusinessTypes.length === 0}
              options={filteredSubBusinessTypes}
              value={selectedSubBusinessType}
              onTyping={handleTypingSubBusinessType}
              onSelect={setSelectedSubBusinessType}
              onHoverColor="primaryKonekto"
              padding="13px"
              placeholder="Pilih Sub Tipe Bisnis"
              width="300px"
              hideHelperText={!isErrorSubBusinessType}
              helperText={errorSubBusinessType}
              error={isErrorSubBusinessType}
              withIcon={{
                icon: (
                  <Icon isActive={focusField === 'sub_business_type'}>
                    <Arrow
                      transform-origin="50% 50%"
                      transform={`rotate(${
                        focusField === 'sub_business_type' ? 0 : 180
                      })`}
                    />
                  </Icon>
                ),
                position: 'right',
              }}
              borderActiveColor={theme.colors.primaryKonekto}
            />
          </InputGroup>
        )}
        <FilterBottom>
          <StyledButton
            color="grayLight"
            letterCase="capitalize"
            margin="0 30px 0 0"
            variant="outlined"
            width="223px"
            onClick={resetFilter}
          >
            Reset
          </StyledButton>
          <Button
            color="primaryKonekto"
            letterCase="capitalize"
            width="223px"
            onClick={submitFilter}
          >
            Submit
          </Button>
        </FilterBottom>
      </FilterContainer>
    </Drawer>
  )
}
export default FilterDrawer
